// AIRecipeSetup — MCM-styled setup screen for the AI Recipe flow.
//
// Two-tier layout:
//
//   ┌──────────── WarmBackdrop ────────────┐
//   │  back  ·  AI Recipe                  │
//   │                                      │
//   │  resume-draft banner (if applicable) │
//   │                                      │
//   │  ╭───────── glass form panel ───────╮ │
//   │  │  What are we making?             │ │
//   │  │  pantry signal line              │ │
//   │  │                                  │ │
//   │  │  MEAL PROMPT  (textarea)         │ │
//   │  │  MUST INCLUDE (collapsible)      │ │
//   │  │                                  │ │
//   │  │  [ Draft recipe ]                │ │
//   │  │  Roll the dice · Surprise me     │ │
//   │  │                                  │ │
//   │  │  ▸ Refine — summary chip         │ │
//   │  │    (expands to two sub-cards:    │ │
//   │  │     Dish shape · Effort)         │ │
//   │  ╰──────────────────────────────────╯ │
//   └────────────────────────────────────────┘
//
// Above-fold = required input + primary action. Refine is opt-in for
// users who want to steer course/cuisine/timing/difficulty/priority.
// Default values are shown as a one-line summary so collapsed reads as
// informative, not hidden.

import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  GlassPanel, PrimaryButton, GhostButton,
  Kicker, SerifHeader, BackChip, FadeIn, HairlineRule, withAlpha,
} from "./primitives";
import { useTheme, THEME_TRANSITION } from "./theme";
import { font, space, radius } from "./tokens";

// Chip configs mirror AIRecipe.jsx — single source of truth lives
// there; this file just renders them in MCM style.
const CUISINE_CHIPS = [
  { id: "any",      label: "Any cuisine"   },
  { id: "italian",  label: "Italian"       },
  { id: "french",   label: "French"        },
  { id: "mexican",  label: "Mexican"       },
  { id: "american", label: "American"      },
  { id: "japanese", label: "Japanese"      },
  { id: "thai",     label: "Thai"          },
  { id: "indian",   label: "Indian"        },
];
const TIME_CHIPS = [
  { id: "quick",  label: "≤30 min" },
  { id: "medium", label: "≤60 min" },
  { id: "long",   label: "Long cook" },
];
const DIFFICULTY_CHIPS = [
  { id: "easy",     label: "Easy"    },
  { id: "medium",   label: "Medium"  },
  { id: "advanced", label: "Advanced"},
];
// null = Auto: the classifier infers portions from the occasion
// ("date night" → 2, "dinner party" → 4-6). An explicit pick here
// overrides that inference and lands as a HARD CONSTRAINT (SERVES=N)
// so quantities are authored for the right table, not rescaled after.
const SERVES_CHIPS = [
  { id: "auto", label: "Auto" },
  { id: "1",    label: "1" },
  { id: "2",    label: "2" },
  { id: "4",    label: "4" },
  { id: "6",    label: "6" },
  { id: "8",    label: "8" },
];
const MEAL_TIMING_CHIPS = [
  { id: "any",       label: "Any time" },
  { id: "breakfast", label: "Breakfast" },
  { id: "lunch",     label: "Lunch" },
  { id: "dinner",    label: "Dinner" },
];
const COURSE_CHIPS = [
  { id: "any",       label: "Any course" },
  { id: "main",      label: "Main" },
  { id: "side",      label: "Side" },
  { id: "dessert",   label: "Dessert" },
  { id: "appetizer", label: "Appetizer" },
  { id: "bake",      label: "Baked Goods" },
  { id: "prep",      label: "Prep" },
];
const PRIORITY_CHIPS = [
  { id: "classic",      label: "Classic",       sub: "Meal prompt rules" },
  { id: "chefs_choice", label: "Chef's choice", sub: "Free hand" },
  { id: "pantry",       label: "Kitchen-only",  sub: "Strict, no shopping" },
];

// Look up a chip's display label by id — used to compose the Refine
// summary line so a non-default value reads like prose, not a slug.
function labelFor(options, id) {
  const hit = options.find(o => o.id === id);
  return hit ? hit.label : null;
}

// One-line summary of the user's current Refine selections, default
// values omitted. Returns "" when everything is at default so the
// collapsed Refine row can fall back to "Default settings."
// Priority deliberately omitted — it's surfaced above the fold as the
// "Mode" picker so users see the system's load-bearing knob before
// they hit Draft. Including it here would also make the summary
// double-count what the user already sees.
function refineSummary({ course, cuisine, mealTiming, time, difficulty, serves }) {
  const parts = [];
  if (course && course !== "any") parts.push(labelFor(COURSE_CHIPS, course));
  if (cuisine && cuisine !== "any") parts.push(labelFor(CUISINE_CHIPS, cuisine));
  if (mealTiming && mealTiming !== "any") parts.push(labelFor(MEAL_TIMING_CHIPS, mealTiming));
  if (time && time !== "medium") parts.push(labelFor(TIME_CHIPS, time));
  if (difficulty && difficulty !== "medium") parts.push(labelFor(DIFFICULTY_CHIPS, difficulty));
  if (serves) parts.push(`Serves ${serves}`);
  return parts.filter(Boolean).join(" · ");
}

// Selectable pill — single primitive used for every chip row. Tone
// drives the active accent (one MCM color per axis); inactive state
// is a neutral hairline-bordered rest. whileTap gives a subtle scale
// tick so the pill feels physically pressed; whileHover is BANNED
// per the no-framer-whileHover rule (it eats first taps on iOS).
function ChipPill({ active, label, sub, tone = "teal", onClick, size = "md" }) {
  const { theme } = useTheme();
  const accent = theme.color[tone] || theme.color.teal;
  const padY = size === "sm" ? 6  : 8;
  const padX = size === "sm" ? 12 : 14;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.94 }}
      transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
      style={{
        display: "inline-flex",
        flexDirection: sub ? "column" : "row",
        alignItems: sub ? "flex-start" : "center",
        gap: sub ? 2 : 6,
        padding: `${padY}px ${padX}px`,
        borderRadius: radius.pill,
        border: `1px solid ${active ? accent : theme.color.hairline}`,
        background: active
          ? withAlpha(accent, 0.12)
          : theme.color.glassFillLite,
        color: active ? accent : theme.color.ink,
        cursor: "pointer",
        fontFamily: font.sans,
        fontSize: 13,
        fontWeight: active ? 500 : 400,
        letterSpacing: "0.01em",
        boxShadow: active
          ? `0 1px 2px ${withAlpha(accent, 0.18)}`
          : `0 1px 2px rgba(30,30,30,0.04)`,
        touchAction: "manipulation",
        ...THEME_TRANSITION,
      }}
    >
      <span>{label}</span>
      {sub && (
        <span style={{
          fontFamily: font.detail, fontStyle: "italic",
          fontSize: 11, color: active ? accent : theme.color.inkFaint,
          fontWeight: 500, letterSpacing: 0,
        }}>
          {sub}
        </span>
      )}
    </motion.button>
  );
}

// Chip row with staggered child mount. Each chip lands ~40ms after
// the previous so a row of 7 reads as a sequential cascade rather
// than a slab. Disabled when `stagger` is false (e.g. in the always-
// visible Must Include section, where staggering on every keystroke
// would feel busy).
function ChipRow({ value, onChange, options, tone = "teal", size = "md", stagger = true }) {
  return (
    <div style={{
      display: "flex", flexWrap: "wrap",
      gap: space.tight,
    }}>
      {options.map((o, i) => {
        const pill = (
          <ChipPill
            key={o.id}
            active={value === o.id}
            label={o.label}
            sub={o.sub}
            tone={tone}
            size={size}
            onClick={() => onChange(o.id)}
          />
        );
        if (!stagger) return pill;
        return (
          <motion.div
            key={o.id}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.22, delay: i * 0.035, ease: [0.22, 1, 0.36, 1] }}
            style={{ display: "inline-flex" }}
          >
            {pill}
          </motion.div>
        );
      })}
    </div>
  );
}

function Section({ kicker, tone, children, last }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: space.flow,
      paddingBottom: last ? 0 : space.gap,
    }}>
      <Kicker tone={tone}>{kicker}</Kicker>
      {children}
    </div>
  );
}

// Sub-card inside the Refine disclosure. Two of these stack: "Dish
// shape" (course / cuisine / meal timing) and "Effort" (time /
// difficulty / priority).
function RefineCard({ title, tone, children }) {
  const { theme } = useTheme();
  return (
    <div style={{
      padding: space.flow,
      borderRadius: radius.field,
      background: theme.color.glassFillHeavy,
      border: `1px solid ${theme.color.hairline}`,
      display: "flex", flexDirection: "column", gap: space.flow,
      ...THEME_TRANSITION,
    }}>
      <Kicker tone={tone}>{title}</Kicker>
      {children}
    </div>
  );
}

// Star-ingredients picker — collapsible. Header shows count + first
// few selected; tapping opens a search-filterable list grouped by
// category. Each group is a horizontal chip ribbon, capped at 5
// visible items per category until the user hits "Show all".
const STAR_GROUP_CAP = 5;
function StarPicker({ groups, selectedIds, setSelectedIds, search, setSearch, accent }) {
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const total = groups.reduce((acc, g) => acc + g.items.length, 0);

  const hasSearch = (search || "").trim().length > 0;

  const filtered = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map(g => ({
        ...g,
        // Match against the augmented search tokens computed in
        // AIRecipe.jsx#starOptionGroups (label + pantry-cut labels +
        // species synonyms like "steak" → beef, "poultry" → chicken).
        // Falls back to a label-include for any opt that didn't carry
        // tokens forward.
        items: g.items.filter(it => {
          if (Array.isArray(it.searchTokens)) {
            return it.searchTokens.some(t => t.includes(q));
          }
          return (it.label || "").toLowerCase().includes(q);
        }),
      }))
      .filter(g => g.items.length > 0);
  }, [groups, search]);

  const toggleGroup = (key) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  if (total === 0) return null;

  const toggle = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const summaryNames = selectedIds
    .map(id => {
      for (const g of groups) {
        const hit = g.items.find(it => it.id === id);
        if (hit) return hit.label;
      }
      return null;
    })
    .filter(Boolean);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: space.tight }}>
      <motion.button
        type="button"
        onClick={() => setOpen(o => !o)}
        whileTap={{ scale: 0.99 }}
        style={{
          display: "flex", alignItems: "center", gap: space.tight,
          padding: `${space.tight}px ${space.flow}px`,
          borderRadius: radius.field,
          border: `1px solid ${selectedIds.length > 0 ? accent : theme.color.hairline}`,
          background: theme.color.glassFillLite,
          color: theme.color.ink,
          cursor: "pointer",
          textAlign: "left",
          fontFamily: font.sans, fontSize: 14,
          touchAction: "manipulation",
          ...THEME_TRANSITION,
        }}
      >
        <span style={{ flex: 1 }}>
          {selectedIds.length === 0
            ? "None — Claude picks the lead"
            : summaryNames.join(" · ")}
        </span>
        <span style={{ fontFamily: font.mono, fontSize: 10, color: accent, letterSpacing: "0.12em" }}>
          {selectedIds.length > 0 ? `${selectedIds.length} ★` : "TAP TO PICK"}
        </span>
      </motion.button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="star-body"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div style={{
              padding: space.flow,
              borderRadius: radius.field,
              background: theme.color.glassFillHeavy,
              border: `1px solid ${theme.color.hairline}`,
              display: "flex", flexDirection: "column", gap: space.flow,
              ...THEME_TRANSITION,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: space.tight }}>
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search your kitchen…"
                  style={{
                    flex: 1, minWidth: 0,
                    padding: `${space.tight}px ${space.flow}px`,
                    borderRadius: radius.chip,
                    border: `1px solid ${theme.color.hairline}`,
                    background: theme.color.glassFillHeavy,
                    color: theme.color.ink,
                    fontFamily: font.sans, fontSize: 14,
                    outline: "none",
                    boxShadow: theme.shadow.inputInset,
                  }}
                />
                {selectedIds.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setSelectedIds([])}
                    className="mcm-focusable"
                    style={{
                      padding: `${space.tight}px ${space.flow}px`,
                      borderRadius: radius.chip,
                      border: `1px solid ${theme.color.hairline}`,
                      background: "transparent",
                      color: theme.color.inkMuted,
                      fontFamily: font.mono, fontSize: 10,
                      letterSpacing: "0.12em",
                      cursor: "pointer",
                      touchAction: "manipulation",
                      whiteSpace: "nowrap",
                    }}
                  >
                    CLEAR
                  </button>
                )}
              </div>
              {filtered.length === 0 ? (
                <div style={{ fontFamily: font.detail, fontStyle: "italic", color: theme.color.inkMuted, fontSize: 13 }}>
                  Nothing in your kitchen matches "{search}".
                </div>
              ) : filtered.map(g => {
                const isExpanded = hasSearch || expandedGroups.has(g.key);
                // Always keep the user's selected items visible — even if
                // they'd otherwise be cut off by the cap. Selected items
                // come first, then the next slots fill with unselected.
                const selectedItems = g.items.filter(it => selectedIds.includes(it.id));
                const unselectedItems = g.items.filter(it => !selectedIds.includes(it.id));
                const ordered = [...selectedItems, ...unselectedItems];
                const hidden = isExpanded ? 0 : Math.max(0, ordered.length - STAR_GROUP_CAP);
                const visible = isExpanded ? ordered : ordered.slice(0, STAR_GROUP_CAP);
                return (
                  <div key={g.key} style={{ display: "flex", flexDirection: "column", gap: space.tight }}>
                    <Kicker>{g.label}</Kicker>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {visible.map(it => {
                        const cutsTxt = Array.isArray(it.cuts) && it.cuts.length > 0
                          ? ` · ${it.cuts.join(", ")}`
                          : "";
                        return (
                          <ChipPill
                            key={it.id}
                            active={selectedIds.includes(it.id)}
                            label={`${it.emoji ? `${it.emoji} ` : ""}${it.label}${cutsTxt}`}
                            tone="mustard"
                            size="sm"
                            onClick={() => toggle(it.id)}
                          />
                        );
                      })}
                      {hidden > 0 && (
                        <button
                          type="button"
                          onClick={() => toggleGroup(g.key)}
                          className="mcm-focusable"
                          style={{
                            padding: "4px 10px",
                            borderRadius: radius.chip,
                            border: `1px dashed ${theme.color.hairline}`,
                            background: "transparent",
                            color: theme.color.inkMuted,
                            fontFamily: font.mono, fontSize: 10,
                            letterSpacing: "0.12em",
                            cursor: "pointer",
                            touchAction: "manipulation",
                          }}
                        >
                          + {hidden} MORE
                        </button>
                      )}
                      {!hasSearch && expandedGroups.has(g.key) && ordered.length > STAR_GROUP_CAP && (
                        <button
                          type="button"
                          onClick={() => toggleGroup(g.key)}
                          className="mcm-focusable"
                          style={{
                            padding: "4px 10px",
                            borderRadius: radius.chip,
                            border: `1px dashed ${theme.color.hairline}`,
                            background: "transparent",
                            color: theme.color.inkMuted,
                            fontFamily: font.mono, fontSize: 10,
                            letterSpacing: "0.12em",
                            cursor: "pointer",
                            touchAction: "manipulation",
                          }}
                        >
                          SHOW LESS
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function AIRecipeSetup({
  // Setup state (controlled — parent owns)
  mealPrompt,         setMealPrompt,
  priority,           setPriority,
  course,             setCourse,
  mealTiming,         setMealTiming,
  cuisine,            setCuisine,
  time,               setTime,
  difficulty,         setDifficulty,
  serves,             setServes,
  starIngredientIds,  setStarIngredientIds,
  starSearch,         setStarSearch,
  starOptionGroups,
  // Pantry signal — used for the kitchen-count line
  pantryCount = 0,
  // Photo-seed pinning. When the user opens AIRecipe via the photo
  // flow (raw ingredients OR finished dish), CreateMenu hands over
  // the identified items + sketch. We render them as chips above the
  // meal-prompt input so the user has visual confirmation the photo
  // seed survived. `photoSeedItems` is the identified ingredient
  // array; `photoSeedSketch` carries the finished-dish metadata
  // (dishName, sauces, techniques) when that mode was used.
  photoSeedItems = null,
  photoSeedSketch = null,
  // CTA
  onStart,
  onCancel,
  // Resume-draft banner
  draftSavedAt,
  draftAgeLabel,
  hasResumableSketch = false,
  hasResumableRecipe = false,
  resumableTitle = "",
  onResume,
  onAbandonDraft,
  // Compose-a-meal — passthrough block from AIRecipe (already styled)
  mealHeaderBlock,
  // SURPRISE ME — pre-pick a concept frame and route to the reveal
  // screen instead of drafting. Optional; when undefined the button
  // doesn't render. Lives next to "Draft recipe" so the user has a
  // visible "I don't know what I want — surprise me" path.
  onSurpriseMe,
  // ROLL THE DICE — one-tap pure-random path. Picks a frame and
  // fires the sketch immediately, no reveal step. The user sees the
  // rolled frame via the FRAMED BY chip on the sketch screen
  // post-draft. Companion to SURPRISE ME for the "just go" mood —
  // SURPRISE ME is for users who want to peek before committing,
  // ROLL THE DICE is for users who want zero ceremony.
  onRollAndDraft,
}) {
  const { theme } = useTheme();
  const showMealTiming = course !== "bake" && course !== "prep";
  const [refineOpen, setRefineOpen] = useState(false);

  // "Kitchen-only" is meaningless with an empty pantry (kitchen-
  // tracking off passes pantry=[], and genuinely empty kitchens hit
  // the same wall) — a strict use-what-you-have draft against zero
  // items is degenerate by construction. Hide the chip, and snap any
  // stale "pantry" priority (resumed drafts restore it verbatim)
  // back to the default mode.
  const modeChips = pantryCount > 0
    ? PRIORITY_CHIPS
    : PRIORITY_CHIPS.filter((c) => c.id !== "pantry");
  useEffect(() => {
    if (pantryCount === 0 && priority === "pantry") setPriority("classic");
  }, [pantryCount, priority, setPriority]);

  const summary = useMemo(
    () => refineSummary({ course, cuisine, mealTiming, time, difficulty, serves }),
    [course, cuisine, mealTiming, time, difficulty, serves],
  );

  return (
    <div style={{
      position: "relative", minHeight: "100dvh",
      background: theme.color.cream,
      paddingBottom: 80,
      ...THEME_TRANSITION,
    }}>
      {/* WarmBackdrop is provided by the host (App root or Showcase
          wrapper). Mounting a second one here stacked two backdrops. */}

      {/* Top bar */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10,
        padding: `calc(env(safe-area-inset-top, 0px) + 12px) 16px 12px`,
        display: "flex", alignItems: "center", gap: space.inline,
        background: `linear-gradient(180deg, ${withAlpha(theme.color.cream, 0.92)} 0%, ${withAlpha(theme.color.cream, 0)} 100%)`,
        ...THEME_TRANSITION,
      }}>
        {onCancel && (
          <BackChip onClick={onCancel}>← Back</BackChip>
        )}
        <Kicker>AI Recipe</Kicker>
      </div>

      <div style={{
        position: "relative", zIndex: 1,
        maxWidth: 600, margin: "0 auto",
        padding: `${space.gap}px ${space.flow}px`,
      }}>
        {/* Compose-a-meal sticky header — rendered as-is from parent */}
        {mealHeaderBlock}

        {/* Resume-draft banner */}
        {draftSavedAt && (hasResumableSketch || hasResumableRecipe) && (
          <FadeIn>
            <div style={{
              marginBottom: space.gap,
              padding: `${space.flow}px ${space.flow}px`,
              borderRadius: radius.field,
              background: withAlpha(theme.color.mustard, 0.10),
              border: `1px solid ${withAlpha(theme.color.mustard, 0.32)}`,
              display: "flex", alignItems: "center", gap: space.inline,
              flexWrap: "wrap",
              ...THEME_TRANSITION,
            }}>
              <span style={{
                flex: 1, minWidth: 200,
                fontFamily: font.detail, fontStyle: "italic",
                fontSize: 14, color: theme.color.warmBrown,
              }}>
                {hasResumableRecipe
                  ? `Unsaved recipe "${resumableTitle}" · saved ${draftAgeLabel?.(draftSavedAt) || "moments ago"}`
                  : `Draft saved ${draftAgeLabel?.(draftSavedAt) || "moments ago"} — pick up where you left off`}
              </span>
              <div style={{ display: "flex", gap: space.tight }}>
                {onResume && (
                  <PrimaryButton onClick={onResume} style={{ fontSize: 14, padding: `${space.tight}px ${space.flow}px` }}>
                    Resume
                  </PrimaryButton>
                )}
                {onAbandonDraft && (
                  <GhostButton onClick={onAbandonDraft} style={{ fontSize: 14, padding: `${space.tight}px ${space.flow}px` }}>
                    Start fresh
                  </GhostButton>
                )}
              </div>
            </div>
          </FadeIn>
        )}

        <FadeIn>
          <motion.div layout transition={{ layout: { duration: 0.32, ease: [0.22, 1, 0.36, 1] } }}>
          <GlassPanel padding={space.gap}>
            <SerifHeader size={32}>What are we making?</SerifHeader>
            <div style={{
              marginTop: space.tight,
              fontFamily: font.detail, fontStyle: "italic",
              fontSize: 15, color: theme.color.inkMuted, lineHeight: 1.45,
            }}>
              {photoSeedItems && photoSeedItems.length > 0
                ? (photoSeedSketch
                    ? `Working from your photo — ${photoSeedItems.length} ${photoSeedItems.length === 1 ? "ingredient" : "ingredients"} identified, plus the techniques and finishing I saw.`
                    : `Working from your photo — ${photoSeedItems.length} ${photoSeedItems.length === 1 ? "ingredient" : "ingredients"} identified. I'll build the recipe around what's in the shot.`)
                : pantryCount === 0
                  ? "Your kitchen is empty — I'll lean on staples."
                  : `I'll look at ${pantryCount} ${pantryCount === 1 ? "item" : "items"} in your kitchen and shape a recipe around what you tell me.`}
            </div>

            {/* Photo-seed pinning. Shows the identified items as chips
                so the user has visible confirmation the photo flow
                handed off correctly. For finished-dish mode we also
                render the sauces / techniques as a second row so the
                user can spot a misread (chimichurri vs salsa verde,
                etc.) before drafting. */}
            {photoSeedItems && photoSeedItems.length > 0 && (
              <>
                <div style={{ height: space.tight }} />
                <div style={{
                  display: "flex", flexWrap: "wrap", gap: 6,
                  marginTop: 6,
                }}>
                  {photoSeedItems.map((it, i) => (
                    <span
                      key={`${it.canonicalId || it.name}-${i}`}
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "6px 10px",
                        borderRadius: 999,
                        background: theme.color.glassFillHeavy,
                        border: `1px solid ${theme.color.hairline}`,
                        color: theme.color.ink,
                        fontFamily: font.sans, fontSize: 12, fontWeight: 500,
                      }}
                    >
                      <span style={{ fontSize: 14 }}>{it.emoji || "🍽️"}</span>
                      {it.name}
                    </span>
                  ))}
                </div>
                {photoSeedSketch && (
                  <div style={{
                    marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6,
                  }}>
                    {(photoSeedSketch.techniques || []).map((t, i) => (
                      <span
                        key={`tech-${i}`}
                        style={{
                          padding: "4px 9px",
                          borderRadius: 999,
                          background: "transparent",
                          border: `1px dashed ${theme.color.hairline}`,
                          color: theme.color.inkMuted,
                          fontFamily: font.sans, fontSize: 11,
                          letterSpacing: "0.04em",
                        }}
                      >
                        {String(t).replace(/_/g, " ")}
                      </span>
                    ))}
                    {(photoSeedSketch.sauces || []).filter((s) => s && s.name).map((s, i) => (
                      <span
                        key={`sauce-${i}`}
                        style={{
                          padding: "4px 9px",
                          borderRadius: 999,
                          background: "transparent",
                          border: `1px dashed ${theme.color.hairline}`,
                          color: theme.color.inkMuted,
                          fontFamily: font.sans, fontSize: 11,
                          letterSpacing: "0.04em",
                        }}
                      >
                        sauce · {String(s.name).replace(/_/g, " ")}
                      </span>
                    ))}
                    {photoSeedSketch.finishing?.type && photoSeedSketch.finishing.type !== "none" && (
                      <span
                        style={{
                          padding: "4px 9px",
                          borderRadius: 999,
                          background: "transparent",
                          border: `1px dashed ${theme.color.hairline}`,
                          color: theme.color.inkMuted,
                          fontFamily: font.sans, fontSize: 11,
                          letterSpacing: "0.04em",
                        }}
                      >
                        finish · {String(photoSeedSketch.finishing.type).replace(/_/g, " ")}
                      </span>
                    )}
                  </div>
                )}
              </>
            )}

            <div style={{ height: space.gap }} />
            <HairlineRule />

            {/* HERO — the only required input + the must-include picker */}
            <div style={{ display: "flex", flexDirection: "column", gap: space.gap, marginTop: space.gap }}>
              <Section kicker="Meal prompt" tone={theme.color.warmBrown}>
                <textarea
                  value={mealPrompt}
                  onChange={(e) => setMealPrompt(e.target.value)}
                  placeholder='e.g. "Italian Sunday-dinner energy" · "Light breakfast with the eggs" · "Dessert before the ricotta turns"'
                  rows={3}
                  style={{
                    width: "100%", boxSizing: "border-box",
                    padding: `${space.flow}px ${space.flow}px`,
                    borderRadius: radius.field,
                    border: `1px solid ${theme.color.hairline}`,
                    background: theme.color.glassFillHeavy,
                    color: theme.color.ink,
                    fontFamily: font.sans, fontSize: 15,
                    lineHeight: 1.5,
                    outline: "none",
                    resize: "vertical",
                    boxShadow: theme.shadow.inputInset,
                    ...THEME_TRANSITION,
                  }}
                />
              </Section>

              {/* MODE — this is the system's load-bearing knob (how
                  Claude balances meal prompt vs. pantry). Lives above
                  the fold deliberately: users need to grasp it before
                  they tap Draft. The italic sub-line on each chip
                  ("Meal prompt rules" / "Free hand" / "Strict, no
                  shopping") is the explainer — that's why these chips
                  carry `sub` and the others don't. */}
              <Section kicker="Mode" tone={theme.color.teal}>
                <ChipRow
                  value={priority}
                  onChange={setPriority}
                  options={modeChips}
                  tone="teal"
                  stagger={false}
                />
              </Section>

              {starOptionGroups && starOptionGroups.length > 0 && (
                <Section kicker="Must include" tone={theme.color.mustard} last>
                  <StarPicker
                    groups={starOptionGroups}
                    selectedIds={starIngredientIds}
                    setSelectedIds={setStarIngredientIds}
                    search={starSearch}
                    setSearch={setStarSearch}
                    accent={theme.color.mustard}
                  />
                </Section>
              )}
            </div>

            <div style={{ height: space.gap }} />
            <PrimaryButton onClick={onStart} style={{ width: "100%" }}>
              Draft recipe
            </PrimaryButton>
            {(onSurpriseMe || onRollAndDraft) && (
              <>
                <div style={{ height: space.inline }} />
                <div style={{ display: "flex", gap: space.inline }}>
                  {onRollAndDraft && (
                    <GhostButton onClick={onRollAndDraft} style={{ flex: 1 }}>
                      Roll the dice
                    </GhostButton>
                  )}
                  {onSurpriseMe && (
                    <GhostButton onClick={onSurpriseMe} style={{ flex: 1 }}>
                      Surprise me
                    </GhostButton>
                  )}
                </div>
              </>
            )}

            <div style={{ height: space.gap }} />
            <HairlineRule />

            {/* REFINE — collapsed by default. Header shows the
                non-default selections so the user can see what they've
                set without expanding. Two sub-cards inside group six
                axes by intent: dish-shape vs. execution-effort. */}
            <div style={{ marginTop: space.gap }}>
              <motion.button
                type="button"
                onClick={() => setRefineOpen(o => !o)}
                whileTap={{ scale: 0.99 }}
                style={{
                  width: "100%",
                  display: "flex", alignItems: "center", gap: space.inline,
                  padding: `${space.tight}px ${space.flow}px`,
                  borderRadius: radius.field,
                  border: `1px solid ${theme.color.hairline}`,
                  background: theme.color.glassFillLite,
                  color: theme.color.ink,
                  cursor: "pointer",
                  textAlign: "left",
                  fontFamily: font.sans, fontSize: 14,
                  touchAction: "manipulation",
                  ...THEME_TRANSITION,
                }}
              >
                <span style={{
                  fontFamily: font.mono, fontSize: 10,
                  color: theme.color.inkMuted, letterSpacing: "0.14em",
                }}>
                  REFINE
                </span>
                <span style={{
                  flex: 1,
                  fontFamily: font.detail, fontStyle: "italic",
                  fontSize: 14, color: theme.color.inkMuted,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {summary || "Default settings — Claude picks the rest"}
                </span>
                <motion.span
                  animate={{ rotate: refineOpen ? 90 : 0 }}
                  transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                  style={{
                    display: "inline-block",
                    fontFamily: font.mono, fontSize: 14,
                    color: theme.color.inkMuted,
                  }}
                >
                  ▸
                </motion.span>
              </motion.button>

              <AnimatePresence initial={false}>
                {refineOpen && (
                  <motion.div
                    key="refine-body"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                    style={{ overflow: "hidden" }}
                  >
                    <div style={{
                      display: "flex", flexDirection: "column",
                      gap: space.flow,
                      paddingTop: space.flow,
                    }}>
                      {/* DISH SHAPE — what the dish IS */}
                      <RefineCard title="Dish shape" tone={theme.color.burnt}>
                        <Section kicker="Course" tone={theme.color.burnt}>
                          <ChipRow
                            value={course}
                            onChange={setCourse}
                            options={COURSE_CHIPS}
                            tone="burnt"
                          />
                        </Section>
                        <Section kicker="Cuisine" tone={theme.color.aqua}>
                          <ChipRow
                            value={cuisine}
                            onChange={setCuisine}
                            options={CUISINE_CHIPS}
                            tone="aqua"
                          />
                        </Section>
                        {showMealTiming && (
                          <Section kicker="Meal timing" tone={theme.color.mustard} last>
                            <ChipRow
                              value={mealTiming}
                              onChange={setMealTiming}
                              options={MEAL_TIMING_CHIPS}
                              tone="mustard"
                            />
                          </Section>
                        )}
                      </RefineCard>

                      {/* EFFORT — how it cooks. Priority/Mode lives
                          above the fold (it's the system's mental
                          model), so this card is just Time + Difficulty. */}
                      <RefineCard title="Effort" tone={theme.color.teal}>
                        <Section kicker="Time" tone={theme.color.teal}>
                          <ChipRow
                            value={time}
                            onChange={setTime}
                            options={TIME_CHIPS}
                            tone="teal"
                          />
                        </Section>
                        <Section kicker="Difficulty" tone={theme.color.mustard}>
                          <ChipRow
                            value={difficulty}
                            onChange={setDifficulty}
                            options={DIFFICULTY_CHIPS}
                            tone="mustard"
                          />
                        </Section>
                        <Section kicker="Serves" tone={theme.color.aqua} last>
                          <ChipRow
                            value={serves ? String(serves) : "auto"}
                            onChange={(id) => setServes?.(id === "auto" ? null : Number(id))}
                            options={SERVES_CHIPS}
                            tone="aqua"
                          />
                        </Section>
                      </RefineCard>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </GlassPanel>
          </motion.div>
        </FadeIn>
      </div>
    </div>
  );
}
